// import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../AuthContext";
import { publicAxios } from "../utils/axios";
import toast from "react-hot-toast";

function Login() {
  const [form, setForm] = useState({
    phone: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (user && token) {
      const userData = JSON.parse(user);
      authContext.signin(userData._id, () => {
        navigate("/", { replace: true });
      });
    }
  }, [navigate, authContext]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.phone.trim()) nextErrors.phone = "Phone number is required";
    if (!form.password) nextErrors.password = "Password is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const loginUser = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please enter phone and password");
      return;
    }

    setSubmitting(true);
    try {
      const response = await publicAxios.post("/auth/login", form);
      const { data } = response.data;

      if (data?.user && data?.token) {
        // Store token and user data
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Update auth context
        authContext.signin(data.user._id, () => {
          toast.success("Login successful!");
          navigate("/", { replace: true });
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Login failed. Please try again."
      );
      console.error("Login error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F5F8F6]">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-2/5 relative flex-col justify-between bg-[#0F3D24] text-white overflow-hidden">
        {/* Ambient background texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#32A650]/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-16 h-72 w-72 rounded-full bg-[#32A650]/10 blur-3xl" />

        <div className="relative px-10 pt-12 xl:px-14 xl:pt-16">
          <div className="flex items-center gap-3">
            <img
              className="h-11 w-11 rounded-lg object-cover ring-1 ring-white/15"
              src={require("../assets/janani_logo.jpg")}
              alt="Janani Group"
            />
            <div className="leading-tight">
              <p className="font-semibold tracking-wide text-white">
                Janani Group
              </p>
              <p className="text-xs text-white/50">Internal Operations</p>
            </div>
          </div>
        </div>

        <div className="relative px-10 xl:px-14">
          <h1 className="text-[2.35rem] xl:text-[2.65rem] font-bold leading-[1.12] tracking-tight text-balance">
            Motor parts inventory,
            <br />
            tracked end to end.
          </h1>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-white/60">
            One system of record for every SKU — from purchase order to
            store shelf to expense report.
          </p>

          <dl className="mt-10 grid grid-cols-3 gap-6 max-w-sm border-t border-white/10 pt-6">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-white/40">
                Stock
              </dt>
              <dd className="mt-1 text-sm font-medium text-white/80">
                 SKU status
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-white/40">
                Purchases
              </dt>
              <dd className="mt-1 text-sm font-medium text-white/80">
                Order tracking
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-white/40">
                Reports
              </dt>
              <dd className="mt-1 text-sm font-medium text-white/80">
                Expense &amp; audit
              </dd>
            </div>
          </dl>
        </div>

        <div className="relative px-10 pb-10 xl:px-14 xl:pb-12">
          <p className="text-xs text-white/35">
            Access is restricted to authorized Janani Group staff.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[380px]">
          {/* Mobile Logo and Title */}
          <div className="lg:hidden text-center mb-10">
            <img
              className="h-14 w-14 rounded-lg object-cover mx-auto mb-4 ring-1 ring-black/5"
              src={require("../assets/janani_logo.jpg")}
              alt="Janani Group"
            />
            <h1 className="text-xl font-bold text-gray-900">Janani Group</h1>
            <p className="text-sm text-gray-500">
              Motor Parts Inventory System
            </p>
          </div>

          <div className="mb-8">
            <h2 className="text-[1.75rem] font-bold text-gray-900 tracking-tight">
              Sign in
            </h2>
            <p className="mt-1.5 text-[15px] text-gray-500">
              Enter your credentials to access the inventory portal.
            </p>
          </div>

          <form onSubmit={loginUser} noValidate className="space-y-5">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Phone number
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                  <svg
                    className="h-[18px] w-[18px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleInputChange}
                  placeholder="01XXXXXXXXX"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                  className={`w-full rounded-lg border bg-white pl-11 pr-4 py-2.5 text-[15px] text-gray-900 placeholder-gray-400 outline-none transition duration-150 focus:ring-4 ${
                    errors.phone
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
                      : "border-gray-300 focus:border-[#32A650] focus:ring-[#32A650]/15"
                  }`}
                />
              </div>
              {errors.phone && (
                <p id="phone-error" className="mt-1.5 text-xs text-red-600">
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                  <svg
                    className="h-[18px] w-[18px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="10" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={
                    errors.password ? "password-error" : undefined
                  }
                  className={`w-full rounded-lg border bg-white pl-11 pr-11 py-2.5 text-[15px] text-gray-900 placeholder-gray-400 outline-none transition duration-150 focus:ring-4 ${
                    errors.password
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/10"
                      : "border-gray-300 focus:border-[#32A650] focus:ring-[#32A650]/15"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:text-[#1B7A3D]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      className="h-[18px] w-[18px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.9 19.9 0 0 1 5.06-5.94M9.9 4.24A10.9 10.9 0 0 1 12 4c7 0 11 8 11 8a19.86 19.86 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      className="h-[18px] w-[18px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1.5 text-xs text-red-600">
                  {errors.password}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2.5 text-sm text-gray-600 select-none">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-[#32A650] focus:ring-[#32A650]/40"
              />
              Remember me on this device
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-[#1B7A3D] py-2.5 text-[15px] font-semibold text-white shadow-sm transition duration-150 hover:bg-[#166432] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#32A650]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Signing in…
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-gray-400">
            Trouble accessing your account? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
